/**
 * AnalyticsTracker — fires a GA4 page_view on every route change.
 *
 * Mounted once inside <BrowserRouter> in App.js. Owns the initial-load
 * page_view too (gtag config uses send_page_view:false), so every view is
 * counted exactly once. Renders nothing.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { initGA, trackPageView } from '@/lib/analytics';
import { agencyEvent, captureUtm, initGTM, initMetaPixel, marketingPageView } from '@/lib/marketing';

export function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    initGA();
    initGTM();        // no-op unless REACT_APP_GTM_ID is set
    initMetaPixel();  // no-op unless REACT_APP_META_PIXEL_ID set + consent given
  }, []);

  // Agency conversions: email_click / phone_click must fire for ANY
  // mailto:/tel: link anywhere on the site (footer, legal pages, future
  // additions). One delegated capture-phase listener covers them all
  // without per-component wiring.
  useEffect(() => {
    const onDocumentClick = (e) => {
      const link = e.target?.closest?.('a[href^="mailto:"], a[href^="tel:"]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href.startsWith('mailto:')) {
        agencyEvent('email_click', 'contact', 'email_address');
      } else {
        agencyEvent('phone_click', 'contact', 'phone_number');
      }
    };
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, []);

  useEffect(() => {
    captureUtm(location.search); // persist utm_*/gclid/fbclid for attribution
    trackPageView(location.pathname + location.search);
    marketingPageView(location.pathname);
  }, [location.pathname, location.search]);

  return null;
}
